import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Icon from '../../../components/AppIcon';
import {
  countActiveSharedMembers,
  getMemberStatus,
  getSharedMembersForDisplay,
  isRemovedLicenseMember,
} from '../../../utils/licenseMembers';

const formatBrazilianDate = (isoDate) => {
  if (!isoDate) return '—';

  return new Date(isoDate).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
  });
};

const StatusBadge = ({ status }) => {
  if (status === 'pending') {
    return (
      <span className="text-xs font-medium text-yellow-700">
        Aguardando resposta
      </span>
    );
  }

  if (status === 'removed') {
    return (
      <span className="text-xs font-medium text-muted-foreground">
        Removido
      </span>
    );
  }

  if (status === 'created' || status === 'active') {
    return (
      <span className="text-xs font-medium text-green-600">
        Adicionado
      </span>
    );
  }

  return null;
};

const memberKey = (member) => member.user_id ?? member.email ?? '';

const SharedUsersCard = ({
  members,
  maxMembers,
  canAddMember,
  onAddMember,
  onRevoke,
  onReshare,
}) => {
  const [loadingByUser, setLoadingByUser] = useState({});

  const sharedMembers = getSharedMembersForDisplay(members);
  const activeSharedCount = countActiveSharedMembers(members);

  const runWithLoading = async (key, fn, successMessage, errorMessage) => {
    setLoadingByUser((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await fn();
      toast.success(result?.message ?? successMessage);
    } catch (err) {
      toast.error(err?.message ?? errorMessage);
    } finally {
      setLoadingByUser((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleReshare = async (member) => {
    if (!onReshare) return;
    const key = memberKey(member);
    await runWithLoading(
      key,
      () => onReshare(member),
      'Licença compartilhada novamente.',
      'Erro ao compartilhar novamente.'
    );
  };

  const handleRevokeClick = (member) => {
    if (!onRevoke) return;
    onRevoke(member);
  };

  return (
    <div className="bg-card border rounded-xl p-6 shadow-elevation-sm">
      {/* HEADER */}
      <div className="flex justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">
            Usuários compartilhados
          </h3>
          <p className="text-xs text-muted-foreground">
            {activeSharedCount} de {maxMembers} usuários utilizados
          </p>
        </div>

        <button
          disabled={!canAddMember}
          onClick={onAddMember}
          className="px-3 py-2 bg-primary text-white rounded-md"
        >
          Adicionar membro
        </button>
      </div>

      {/* LIST */}
      <div className="space-y-4">
        {sharedMembers.map((member) => {
          const memberStatus = getMemberStatus(member);
          const isPending = memberStatus === 'pending';
          const isRemoved = isRemovedLicenseMember(member);

          return (
            <div
              key={member.user_id}
              className={[
                'border rounded-lg p-4 flex justify-between items-center',
                isPending && 'border-yellow-400 bg-yellow-50',
                isRemoved && 'opacity-60',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {/* INFO */}
              <div>
                <div className="flex items-center gap-2">
                  <Icon name="User" size={16} />
                  <p className="text-sm font-medium">
                    {member.email}
                  </p>
                </div>

                <p className="text-xs text-muted-foreground mt-1">
                  Compartilhado em:{' '}
                  {formatBrazilianDate(member.added_at)}
                </p>

                {/* STATUS */}
                <div className="mt-1">
                  <StatusBadge status={memberStatus} />
                </div>

                {/* USAGE INFO (ONLY FOR MEMBERS) */}
                {member.role === 'member' && (
                  <div className="flex gap-4 mt-2">
                    <p className="text-xs text-muted-foreground">
                      Documentos Gerados:{' '}
                      <span className="font-medium text-foreground">
                        {member.documents_generated ?? 0}
                      </span>
                    </p>

                    <p className="text-xs text-muted-foreground">
                      Assinaturas de documentos:{' '}
                      <span className="font-medium text-foreground">
                        {member.documents_signed ?? 0}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* ACTION */}
              <div className="flex flex-col items-end gap-2">
                {isRemoved ? (
                  onReshare && (
                    <button
                      type="button"
                      disabled={loadingByUser[memberKey(member)]}
                      onClick={() => handleReshare(member)}
                      className="px-3 py-2 bg-primary text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingByUser[memberKey(member)]
                        ? 'Processando...'
                        : 'Compartilhar novamente'}
                    </button>
                  )
                ) : (
                  memberStatus !== 'pending' && (
                    <button
                      type="button"
                      onClick={() => handleRevokeClick(member)}
                      className="px-3 py-2 bg-destructive text-white rounded-md"
                    >
                      Revogar licença
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}

        {activeSharedCount === 0 && sharedMembers.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nenhum usuário compartilhado ainda.
          </p>
        )}
      </div>
    </div>
  );
};

export default SharedUsersCard;